import React from "react"
import { Helmet } from "react-helmet-async"
import { MONTHS, SCHEMA, RUPEE_SYMBOL, MRP } from "../constants"
import { useNavigate } from "react-router-dom"
import { sanitize } from "isomorphic-dompurify"

export function getAssetPath(assetName) {
    // return process.env.LOCAL_ASSET_PATH + assetName;
    // https://stagassets.1mg.com/pwa-app/staging/2.0.0
    const isDev = process.env.NODE_ENV !== "production",
        serverPath = process.env.LOCAL_ASSET_PATH,
        cdnPath = `${process.env.CDN_ASSET_PATH}/${process.env.S3_FOLDER}/${process.env.BUILD_ENV}/${process.env.APPLICATION}/${process.env.BUILD_NUMBER}/static/images/`
    return (isDev ? serverPath : cdnPath) + assetName
}

export const isServer = !(typeof window !== "undefined" && window.document && window.document.createElement)

export const getExternalOneMgUrl = (url) => {
    return process.env.EXTERNAL_BASE_URL + url
}

export const imagePreloader = (src) => {
    return (
        <Helmet>
            <link rel="preload" as="image" href={src} />
        </Helmet>
    )
}

// Transforms css based hex code to RGB format
export const hexToRGB = (hex) => {
    const red = parseInt(hex.substring(1, 3), 16)
    const green = parseInt(hex.substring(3, 5), 16)
    const blue = parseInt(hex.substring(5, 7), 16)
    return { red, green, blue }
}

export const getCityFromCookie = () => {
    const city = getCookie("city")
    if (city) {
        return city
    } else {
        setCookie("city", "New Delhi", 90)
    }
}

export const setCurrentLocationDetailsCookies = ({
    city,
    latitude,
    longitude,
    pincode,
    polygonIDs,
    addressText,
    addressSubtext,
    addressType,
    demandClusterId,
}) => {
    const EXPIRY_DAYS = 7

    const setLocationCookie = (name, value) => {
        if (typeof window === "undefined") {
            return null
        }
        const date = new Date()
        date.setTime(date.getTime() + EXPIRY_DAYS * 24 * 60 * 60 * 1000)
        const expires = `; expires=${date.toUTCString()}`
        document.cookie = name + "=" + (value || "") + expires + "; path=/"
    }

    setLocationCookie("city", city)
    setLocationCookie("latitude", latitude)
    setLocationCookie("longitude", longitude)
    setLocationCookie("pincode", pincode)
    setLocationCookie("polygonIDs", polygonIDs)
    setLocationCookie("addressText", addressText)
    setLocationCookie("addressSubtext", addressSubtext)
    setLocationCookie("addressType", addressType)
    setLocationCookie("demandClusterId", demandClusterId)
}

export function setCookie(name, value, days) {
    if (typeof window === "undefined") {
        return null
    }
    var expires = ""
    if (days) {
        var date = new Date()
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
        expires = "; expires=" + date.toUTCString()
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/"
}

export function setCookieForHours(name, value, hours) {
    let expires = ""
    if (hours) {
        const date = new Date()
        date.setTime(date.getTime() + hours * 60 * 60 * 1000)
        expires = "; expires=" + date.toUTCString()
        document.cookie = name + "=" + (value || "") + expires + "; path=/"
    }
}

export function getCookie(name) {
    if (typeof window === "undefined") {
        return null
    }
    var nameEQ = name + "="
    var ca = document.cookie.split(";")
    for (var i = 0; i < ca.length; i++) {
        var c = ca[i]
        while (c.charAt(0) == " ") c = c.substring(1, c.length)
        if (c.indexOf(nameEQ) == 0) {
            const value = c.substring(nameEQ.length, c.length)
            try {
                return JSON.parse(decodeURIComponent(value))
            } catch (err) {
                return value
            }
        }
    }
    return null
}

export function getDecodedAffiliateCookie(name) {
    const affiliate = getCookie(name)
    if (affiliate) {
        let affiliateDetails = decodeURIComponent(affiliate)
        if (typeof affiliateDetails === "string") {
            //  since we have directly saved object without using json stringify so we have to manually
            // check and remove unwanted characters.
            if (affiliateDetails.indexOf("j:") > -1) {
                affiliateDetails = affiliateDetails.substr(2)
            }
            affiliateDetails = JSON.parse(affiliateDetails)
        }
        return affiliateDetails._source.toLowerCase()
    }
    return null
}

export const eraseCookie = (name) => {
    document.cookie = name + "=; Max-Age=-99999999; Path=/;"
}

export const deleteAllCookies = () => {
    const cookies = document.cookie.split(";")

    for (var i = 0; i < cookies.length; i++) {
        const cookie = cookies[i]
        const eqPos = cookie.indexOf("=")
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
        // document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/;"
        eraseCookie(name)
    }
}

export const getIdFromSlug = (slug, fromOtc, skipNanCheck = false, fromPatientRecord = false) => {
    let sku = slug?.split("?")[0].split("/").reverse()[0].split("-").reverse()[0]
    if (fromOtc && sku.indexOf("otc") !== -1) {
        sku = sku.substring(3)
    }
    if (fromPatientRecord) {
        let patient_id = slug.split("?")[0].split("/").reverse()[0]
        return patient_id
    }
    if (!skipNanCheck && isNaN(parseInt(sku))) {
        safeLogger.warn({
            message: `SKU ID not a number / not valid so computed SKU ${sku} cannot be fetched `,
            url: slug,
        })
        return null
    }
    return skipNanCheck ? sku : parseInt(sku)
}

export const getId = (url, isReportErrorPage = false) => {
    let sku
    if (isReportErrorPage) {
        sku = url.split("?")[0].split("-").reverse()[1].replace(/^\D+/g, "")
    } else {
        sku = url.split("?")[0].split("-").reverse()[0].replace(/^\D+/g, "")
    }
    if (isNaN(parseInt(sku))) {
        safeLogger.warn({
            message: `SKU ID not a number / not valid so computed SKU ${sku} cannot be fetched `,
            url,
        })
        return null
    }
    return parseInt(sku)
}

export const getPageType = (url) => {
    return url.split("/")[1]
}

export const getDiagnosticsId = (url) => {
    const booking_id = url.split("/")[4]
    const patient_id = url.split("/")[2]
    return {
        booking_id,
        patient_id,
    }
}

export const createSlugFromName = (text) => {
    let slug = text
        .replace(/[^A-Za-z0-9.]/g, "-")
        .replace(/-{2,5}/g, "-")
        .replace(/^-/g, "")
        .replace(/-$/g, "")
        .toLowerCase()
    return slug
}

export const capitalize = (s) => {
    if (typeof s !== "string") return ""
    return s.charAt(0).toUpperCase() + s.slice(1)
}

export const stripHtmlTags = (html) => {
    const tmp = document.createElement("DIV")
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ""
}

export const getUrlParameter = (name, search) => {
    const locationSearch = search || location?.search
    // eslint-disable-next-line no-useless-escape
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]")
    var regex = new RegExp("[\\?&]" + name + "=([^&#]*)")
    var results = regex.exec(locationSearch)
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "))
}

export const getQueryObject = (queryString) => {
    const query = {}
    queryString
        .substring(1)
        .split("&")
        .forEach((param) => {
            const paramSplit = param.split("=")
            if (paramSplit[0]) query[`${paramSplit[0]}`] = paramSplit[1]
        })
    return query
}

/**
 * take query string  ex- a=1&b=2 and return { a:1, b:2 }
 * @param    {String} queryString
 * @return   {Object}
 */
export const getQueryObjectFromString = (queryString) => {
    const query = {}
    queryString.split("&").forEach((param) => {
        const paramSplit = param.split("=")
        if (paramSplit[0]) query[`${paramSplit[0]}`] = paramSplit[1]
    })
    return query
}

export const getQueryString = (query) => {
    let queryString = ""
    queryString =
        queryString +
        Object.keys(query)
            .map((key) => key + "=" + query[key])
            .join("&")
    return queryString
}

export const getDate = (epoch) => {
    const date = new Date(epoch * 1000)
    const day = date.getDate()
    const month = MONTHS[date.getMonth()]
    const year = date.getFullYear()
    if (day && month && year) {
        return `${day} ${month} ${year}`
    } else return null
}

export const isMobileBrowser = () => {
    if (typeof window !== "undefined") {
        if (navigator.userAgent.match(/Android/i)) {
            return "Android"
        }
        if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
            return "iOS"
        }
        return false
    }
    return false
}

export const getBrowserPlatform = (userAgent = "") => {
    if (userAgent?.match(/Android/i)) {
        return "Android"
    }
    if (userAgent?.match(/iPhone|iPad|iPod/i)) {
        return "iOS"
    }
    return "Android"
}

export const setlocalStorageWithExpiry = (key, data, minutes) => {
    if (!key) return
    let skuList = JSON.parse(localStorage.getItem(key)) || []
    if (skuList && skuList.findIndex((x) => x.value == data) < 0) {
        const now = new Date()
        const item = {
            value: data,
            expiry: now.getTime() + minutes * 60000,
        }
        skuList.push(item)
        localStorage.setItem(key, JSON.stringify(skuList))
    }
}

export const checkIfRQPopupShown = (key, id) => {
    if (!key) return
    const skuList = JSON.parse(localStorage.getItem(key))
    const skuIndex = skuList && skuList.length && skuList.findIndex((x) => x.value == id)
    if (skuList && skuIndex > -1) {
        const now = new Date()
        if (now.getTime() > skuList[skuIndex].expiry) {
            skuList.splice(skuIndex, 1)
            !skuList.length
                ? localStorage.removeItem(key)
                : localStorage.setItem(key, JSON.stringify(skuList))
            return false
        } else {
            return true
        }
    } else return false
}

export const trimRupeeSymbol = (val) => String(val || "").replaceAll("₹", "")

export const checkRupeeSymbolAdded = (price, isMRP) => {
    if (!price) return ""
    if (price.toString().includes(RUPEE_SYMBOL)) return price
    return `${isMRP ? `${MRP} ` : ""}${RUPEE_SYMBOL}${price}`
}

export const updateTataCustomerHash = () => {
    if (getCookie("loggedIn") && getCookie("tata_customer_hash")) {
        localStorage.setItem("tata_customer_hash", getCookie("tata_customer_hash"))
    } else {
        localStorage.removeItem("tata_customer_hash")
    }
}

export const getFixedSchema = () => SCHEMA

/**
 * Generate optimized image url
 * @param {string} imageUrl - url of the image.
 * @param {Object} [options] - options related to image optimization.
 * @param {Object} options.width - required width for the optimized image.
 * @param {Object} options.height - required height for the optimized image.
 * @param {Object} enableDPR - flag to check if dpr scaled image is required or not.
 * @returns {string} Optimized image url when possible
 */
export const getOptimizedImageUrl = (
    imageUrl,
    options = { width: "auto", height: "auto" },
    enableDPR = false
) => {
    if (!imageUrl) {
        return
    }
    try {
        const list = ["onemg.gumlet.io"]
        const url = new URL(imageUrl)
        const hostname = url.hostname
        const isOptimizationAvailable = list.includes(hostname)
        if (isOptimizationAvailable) {
            const isGumletUrl = (url) => url.hostname.indexOf("gumlet") !== -1
            const params = new URLSearchParams(url.search)
            if (isGumletUrl(url)) {
                if (enableDPR) {
                    const devicePixelRatio = (typeof window !== "undefined" && window.devicePixelRatio) || 1
                    if (devicePixelRatio > 1) {
                        params.append("dpr", devicePixelRatio)
                    }
                }
                if (options && options.width && options.width !== "auto") {
                    params.append("w", options.width)
                }
                if (options && options.height && options.height !== "auto") {
                    params.append("h", options.height)
                }
                if (!params.has("format")) {
                    params.append("format", "auto")
                }
                url.search = params.toString()
            }
        }
        return url.toString()
    } catch {
        return imageUrl
    }
}

/**
 * Slice one array into multiple arrays dynamically
 * @param {Array} data - data that needs to be sliced.
 * @param {Number} slicingInterval - number of content in a new slice.
 * @param {Number} slicingLimit - number of times the given data needs to be sliced.
 * @param {Number} [startIndex] - optional - index from where to start slicing.
 * @returns {Array} An array containing input data slices is returned.
 */
export const dataSlicer = (data, slicingInterval, slicingLimit = 0, startIndex = 0) => {
    if (!Array.isArray(data) || !slicingInterval) {
        return
    }
    const slicedResults = []
    let count = 0
    while (count <= slicingLimit && startIndex < data.length) {
        let newSlice
        if (count === slicingLimit) {
            newSlice = data.slice(startIndex)
        } else {
            const endIndex = startIndex + slicingInterval
            newSlice = data.slice(startIndex, endIndex)
        }
        if (newSlice) {
            const sliceWithDetails = {
                data: newSlice,
                length: newSlice.length,
                filled: newSlice.length >= slicingInterval,
            }
            slicedResults.push(sliceWithDetails)
        }
        startIndex += slicingInterval
        count += 1
    }
    return slicedResults
}

/**
 * Time formatting utility
 * Case 1: timestamp >= 0 && timestamp < 600 [m:ss]
 * Case 2: timestamp >= 600 && timestamp < 3600 [mm:ss]
 * Case 3: timestamp >= 3600 && timestamp < 36000 [h:mm:ss]
 * Case 4: timestamp >= 36000 [hh:mm:ss]
 */
export const formatTime = (timestamp) => {
    if (!timestamp) return "0:00"
    const duration = Math.trunc(timestamp)
    const hours = Math.trunc(duration / 3600)
    const minutes = Math.trunc(duration / 60)
    const seconds = duration % 60
    const paddedSeconds = ("00" + seconds).slice(-2)

    if (hours > 0) {
        const correctMinutes = minutes >= 60 ? minutes % 60 : minutes
        const paddedMinutes = ("00" + correctMinutes).slice(-2)
        return `${hours}:${paddedMinutes}:${paddedSeconds}`
    }
    return `${minutes}:${paddedSeconds}`
}

/**
 * alternate to getQueryObject, uses URLSearchParams, handles the case when search query includes '=' as a value (DIS-7567)
 * take query string  ex- ?a=1&b=2 and return { a:1, b:2 }
 * @param    {String} queryString
 * @return   {Object}
 */
export const getQueryObjectFromURL = (queryString) => {
    const urlParams = new URLSearchParams(queryString)
    const paramObj = Object.fromEntries(urlParams)
    return paramObj
}

/**
 * retruning object from local storage only if expiry time is not expired
 * @param {key} key of the data stored in localstorage
 * @return {object} json object
 */
export const getUHIDatalocalStorage = (key) => {
    if (!key) return
    const now = new Date()
    let items = JSON.parse(localStorage.getItem(key)) || []
    items = items?.filter((x) => now.getTime() < x.expiry)
    localStorage.setItem(key, JSON.stringify(items))
    return items
}

/**
 * setting local storage data with expiry
 * @param {key} key to be stored in local storage
 * @param {data} data to to be store corrosponding to key
 * @param {minutes} time in minutes to be set as expiry
 * @return {object} json object with value and expiry time
 */
export const setUHIDatalocalStorageWithExpiry = (key, data, minutes) => {
    if (!key) return
    let items = JSON.parse(localStorage.getItem(key)) || []

    const index = items?.findIndex((x) => x.value?.id == data?.id)
    if (index > -1) {
        items?.splice(index, 1)
    }

    const now = new Date()
    const item = {
        value: data,
        expiry: now.getTime() + minutes * 60000,
    }
    items.push(item)
    localStorage.setItem(key, JSON.stringify(items))
}
/**
 * determine if a url is absolute
 * @param {string} - url to be checked
 * @return {Boolean} - true/false
 */
export const isURLAbsolute = (url) => {
    if (!url) {
        return false
    }
    try {
        return Boolean(new URL(url))
    } catch (e) {
        return false
    }
}

export const useRedirect = () => {
    const navigate = useNavigate()
    const handleRedirect = (redirectionUrl, isExternal) => {
        if (isExternal) {
            window.location.href = redirectionUrl
        } else navigate(redirectionUrl)
    }
    return handleRedirect
}
// to check either an object is empty or not
export const isEmpty = (obj = {}) => Object.keys(obj).length === 0

// replicate debounce function of loadsh
export const debounce = (func, delay = 300) => {
    let timeout
    function cancel() {
        if (timeout) {
            clearTimeout(timeout)
        }
    }
    function executed() {
        const later = () => {
            timeout = null
            func.apply(this, arguments)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, delay)
    }
    executed.cancel = cancel
    return executed
}

/**
 * @description Checks if corporate user logs in then returns the relevent redirection url.
 * @returns {String} Redirection url is returned
 */

export const excludeCorporateRedirection = () => {
    let followup = ""
    let corporateDetails = getCookie("corporateDetails") || ""

    const corporateFollowup = (corporateDetails && corporateDetails._redirect_url) || ""

    const currentRoute = window.location.href
    const routesToExclude = [
        "/cart",
        "/order-summary",
        "/chat",
        "/order-with-prescription",
        "/cashless-order-with-prescription",
        "/contactus",
        "/order-payment",
        "/online-doctor-consultation",
    ]
    let hasRoute = false
    routesToExclude.forEach(function (item) {
        if (currentRoute.toLowerCase().indexOf(item.toLowerCase()) != -1) {
            hasRoute = true
        }
    })

    if (!hasRoute && corporateFollowup) {
        followup = corporateFollowup
    }
    return followup
}
/* take an html string and sanitizes it
 * @param    {String} htmlString
 * @return   {String} sanitized html string
 */
export const sanitizer = (htmlString = "", config = {}) => {
    return sanitize(htmlString, config)
}

/**
 * @description Validator lib to validate a specific n number of form fields
 * @param {Object} fields eg: {email: 'test@1mg.com'}
 * @param {Object} errorObj eg : {name : {empty: 'Please enter recipient name'}} this object is added for custom error messages
 * @returns {Object||null} Object of errors or null
 */

function validateField(type, value, fields, errorObj = null) {
    const email =
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    const phoneNumberVal = /^[6-9]\d{9}$/
    const nameRgx = /^[a-zA-Z0-9+. ]+$/

    switch (type) {
        case "email":
            return email.test(value) ? null : "Please enter a valid email address"
        case "phone":
            return phoneNumberVal.test(value) ? null : "Please enter a valid number"
        case "number":
            return phoneNumberVal.test(value) ? null : "Please enter a valid number"
        case "otp":
            return value.length === 6 ? null : "Please enter a 6 digit OTP"
        case "password":
            return value.length > 0 ? null : "Please enter a password"
        case "confirmPassword":
            if (!fields.password && fields.password.length < 1) return "Please enter a password"
            return fields.password === value ? null : "Passwords don't match"
        case "textarea":
            return value.length < 15 ? "Please enter a minimum of 15 characters" : null
        case "name":
            if (!value) {
                return errorObj?.empty || "Please enter recipient name"
            } else if (!nameRgx.test(value)) {
                return errorObj?.regex || "Only alphabets, digits and . are allowed"
            } else if (value.trim() && value.length < 3) {
                return errorObj?.minLength || "Recipient name length should be more than 2"
            } else if (value.trim() && value.length > 100) {
                return errorObj?.maxLength || "Recipient name length should be less than 100"
            } else {
                return errorObj?.default || null
            }
        case "pincode": {
            if (!value) {
                return "Enter pincode"
            } else if (value && value.toString().length != 6) {
                return "Enter a valid pincode"
            } else {
                return null
            }
        }
        case "address":
            return !value || value.length < 10 ? "Please enter the complete address" : null
        case "addressType":
            return value ? null : "Please choose an address type"
        case "addressName":
            return value ? null : "Please enter an address name"
        case "dob": {
            const today = new Date()
            const dob = new Date(value)
            if (dob > today) {
                return "Please enter a valid date of birth"
            }
            return null
        }
        default:
            return null
    }
}

export const Validate = (fields, errorConfig = {}) => {
    if (typeof fields === "object") {
        const validationErrs = {}
        for (let field in fields) {
            const err = validateField(field, fields[field], fields, errorConfig[field])
            err ? (validationErrs[field] = err) : null
        }
        return Object.keys(validationErrs).length ? validationErrs : null
    }
    return `Expected object but got: ${typeof fields}`
}
// Base64 Encoding and Decoding
// Doc - https://developer.mozilla.org/en-US/docs/Glossary/Base64#solution_1_%E2%80%93_escaping_the_string_before_encoding_it
export const utf8_to_b64 = (str = "") => {
    return btoa(unescape(encodeURIComponent(str)))
}
export const b64_to_utf8 = (str = "") => {
    return decodeURIComponent(escape(atob(str)))
}

export const getCartType = (url) => {
    if (typeof url !== "string") {
        throw new Error("Invalid URL. Please provide a valid string URL.")
    }

    const trimmedUrl = url.trim()
    const urlParams = trimmedUrl.split("/")

    if (trimmedUrl.startsWith("/")) {
        urlParams.shift()
    }

    const cartTypeMappings = {
        labs: "labs",
        "done-in-one": "done_in_one",
        "pharmacy-buy-now": "pharmacy_buy_now",
    }

    const cartType = cartTypeMappings[urlParams[0]] || "pharmacy"

    return cartType
}

export const prefixCartTypeIntoUrl = (url, cartType = "pharmacy") => {
    if (!url) {
        throw new Error("URL is required.")
    }

    cartType = cartType.replace(/_/g, "-")
    let trimmedUrl = url.trim()

    if (!trimmedUrl.startsWith("/")) {
        trimmedUrl = `/${trimmedUrl}`
    }

    return cartType === "pharmacy" ? trimmedUrl : `/${cartType}${trimmedUrl}`
}

export const getSource = (cartType, { isCallbackUrl = false } = {}) => {
    const sourceMappings = {
        pharmacy: "PHARMACY",
        // source for DIO is PHARMACY only taking it as DIO in callback to differentiate between
        // DIO and PHARMACY when we get a hit from the payment gateway to our server
        done_in_one: isCallbackUrl ? "DIO" : "PHARMACY",
        labs: isCallbackUrl ? "LABS" : "DIAGNOSTICS",
        pharmacy_buy_now: isCallbackUrl ? "PHARMACY_BUY_NOW" : "PHARMACY",
    }
    const source = sourceMappings[cartType]
    if (source === undefined) {
        throw new Error(`Unsupported cartType: ${cartType}`)
    }

    return source
}

export const getDateInYYYYMMDDformat = (date = new Date()) => {
    let newDate = new Date(date)
    let day = newDate.getDate()
    let month = newDate.getMonth() + 1
    let year = newDate.getFullYear()
    const new_date = `${year}-${month < 10 ? `0${month}` : `${month}`}-${day < 10 ? `0${day}` : `${day}`}`
    return new_date
}

const AddressTypes = {
    Locality: "locality",
    AD_1: "administrative_area_level_1",
    AD_2: "administrative_area_level_2",
    Pincode: "postal_code",
    Country: "country",
}

export const parseGeocodeAddress = (addressDetails) => {
    const { address_components = [] } = addressDetails
    const newAddressObj = {}
    if (address_components?.length) {
        newAddressObj.street2 = addressDetails?.name
        newAddressObj.street3 = addressDetails.formatted_address
        newAddressObj.geodata_latitude = addressDetails.geometry.location.lat
        newAddressObj.geodata_longitude = addressDetails.geometry.location.lng
        newAddressObj.geodata_source = "search_page"
        address_components?.map((ele) => {
            ele.types.map((type) => {
                switch (type) {
                    case AddressTypes.Locality:
                        newAddressObj.city = ele.long_name
                        break

                    case AddressTypes.AD_1:
                        newAddressObj.state = ele.long_name
                        break

                    case AddressTypes.AD_2:
                        if (!newAddressObj.state) {
                            newAddressObj.state = ele.long_name
                        }
                        break

                    case AddressTypes.Pincode:
                        newAddressObj.pincode = ele.long_name
                        break
                    case AddressTypes.Country:
                        newAddressObj.country = ele.long_name
                }
            })
        })
    }
    return newAddressObj
}

// To set cookie for visual announcement bottomsheet and to determine wheather to display bottomsheet
export const labsShowVisualAnnouncementPopUp = (data) => {
    let currentTimeStamp = new Date().getTime()
    let v_announcement_data = getCookie("v_announcement_data")
    let displayPopUpFunction = false
    //to check if v_announcement_data cookie is already present
    if (v_announcement_data) {
        let signatureData = v_announcement_data[data.signature]
        //if v_announcement_data for a particualr signature is present
        if (signatureData) {
            // maxShowCount is the maximum number of times bottomsheet can be shown
            if (data.maxShowCount > signatureData.count) {
                const diffTime = Math.abs(currentTimeStamp - signatureData.timeStamp)
                const diffTotalHours = Math.floor(diffTime / (1000 * 60 * 60))
                // frequencyHours is after how long can bottomsheet be shown again
                if (diffTotalHours >= data.frequencyHours) {
                    //show bottomsheet if diffTotalHours exceeds frequencyHours
                    let dataToSet = {}
                    dataToSet[data.signature] = {
                        count: signatureData.count + 1,
                        frequencyHours: data.frequencyHours,
                        timeStamp: currentTimeStamp,
                    }
                    setCookie("v_announcement_data", JSON.stringify({ ...v_announcement_data, ...dataToSet }))
                    displayPopUpFunction = true
                }
            }
        } else {
            //show bottomsheet if no data present for particualar signature
            let dataToSet = {}
            dataToSet[data.signature] = {
                count: 1,
                frequencyHours: data.frequencyHours,
                timeStamp: currentTimeStamp,
            }
            setCookie("v_announcement_data", JSON.stringify({ ...v_announcement_data, ...dataToSet }))
            displayPopUpFunction = true
        }
    } else {
        //show bottomsheet if no cookie present for v_announcement_data
        let dataToSet = {}
        dataToSet[data.signature] = {
            count: 1,
            frequencyHours: data.frequencyHours,
            timeStamp: currentTimeStamp,
        }
        setCookie("v_announcement_data", JSON.stringify(dataToSet))
        displayPopUpFunction = true
    }
    return displayPopUpFunction
}

export const getAge = (date) => {
    const newDate = new Date(date)
    const bornYear = newDate.getFullYear()
    const currentYear = new Date().getFullYear()

    return `${currentYear - bornYear}`
}
export class TaskScheduler {
    constructor() {
        if (TaskScheduler.instance) {
            return TaskScheduler.instance
        }

        this.runWhenIdle = (callback, ...args) => {
            if ("requestIdleCallback" in window) {
                requestIdleCallback(() => callback(...args))
            } else {
                setTimeout(() => callback(...args), 0)
            }
        }

        this.runJustBeforeRender = (callback, ...args) => {
            requestAnimationFrame(() => {
                callback(...args)
            })
        }

        this.runInNextRender = (callback, ...args) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => callback(...args))
            })
        }

        TaskScheduler.instance = this
    }
}

export const copyTextClipboard = (text) => {
    if (navigator.clipboard) {
        return navigator.clipboard.writeText(text)
    }
}

export const checkDeviceWritePermission = () => {
    return navigator.permissions.query({
        name: "clipboard-write",
        allowWithoutGesture: false,
    })
}

export const checkDeviceReadPermission = () => {
    return navigator.permissions.query({
        name: "clipboard-read",
        allowWithoutGesture: false,
    })
}

export function isViewTransitionPresentOrNot(userAgent = "") {
    const androidPlatform = /Android/.test(userAgent)
    const chromeBrowser = /Chrome/.test(userAgent)
    const chromeVersion = userAgent?.match(/Chrome\/(\d+(\.\d+)?(\.\d+)?(\.\d+)?)/)
    if (androidPlatform && chromeBrowser && chromeVersion) {
        const majorVersion = parseFloat(chromeVersion[1])
        return majorVersion >= 111
    }
    return false
}

export const classNames = (...classes) => {
    return classes
        .flatMap((cls) => {
            if (typeof cls === "string") {
                return cls
            } else if (typeof cls === "object" && cls !== null) {
                return Object.entries(cls)
                    .filter(([_, value]) => value) // eslint-disable-line
                    .map(([key, _]) => key) // eslint-disable-line
            }
            return []
        })
        .join(" ")
}

export const isTCPWebview = (req) => {
    const xTcpPlatform = "x-tcp-platform"
    const tcpPlatform =
        req?.headers?.[xTcpPlatform] || req?.cookies?.[xTcpPlatform] || getCookie(xTcpPlatform) || ""
    return !!(
        tcpPlatform?.toLowerCase()?.indexOf("android") >= 0 || tcpPlatform?.toLowerCase()?.indexOf("ios") >= 0
    )
}

export const isTCPSuperCartJourney = (req) => {
    const tcpCartFlow = req?.query?.tcp_flow || req?.cookies?.tcp_flow || getCookie("tcp_flow")
    return isTCPWebview(req) && tcpCartFlow === "super_cart"
}

export const setTCPAffiliateChannel = (req) => {
    if (isTCPSuperCartJourney(req)) {
        req.headers["x-affiliate-source-channel"] = "TCP_SuperCart"
    }
}

const logMessage = (level) => (msg, opts) => {
    if (typeof window !== "undefined" || typeof logger == "undefined") {
        // eslint-disable-next-line no-console
        console[level]({
            ...msg,
            ...opts,
        })
    } else {
        logger[level](msg, opts)
    }
}

export const safeLogger = {
    info: logMessage("info"),
    warn: logMessage("warn"),
    debug: logMessage("debug"),
    error: logMessage("error"),
}

export function removeEmptyFields(obj) {
    const cleaned = {}
    for (const key in obj) {
        if (
            Object.prototype.hasOwnProperty.call(obj, key) &&
            obj[key] !== "" &&
            obj[key] !== null &&
            obj[key] !== undefined
        ) {
            cleaned[key] = obj[key]
        }
    }
    return cleaned
}
